import { isHotkey } from 'is-hotkey';
import type React from 'react';
import { useRef, useState } from 'react';
import { styled } from 'styled-components';

import CloseIcon from '../icons/FilterClearIcon';
import { searchBarFilterConfigs } from '../searchbar.config';
import type {
  SearchBarFilter,
  SearchBarFilterOption,
} from '@fabric-app/business/search/searchbar.types';
import FilterCombobox from './FilterCombobox';
import { useSearchBarActions } from './SearchBarActionsProvider';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { FilterDropdownMobile } from './FilterComboboxMobile';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { chainShortcutHandlers } from '@fabric-app/business/keyboardShortcuts/utils';
import { isInteractiveElementOrHasInteractiveParent } from '@fabric-app/utils/element';
import { windowBlur } from '@fabric-app/business/utils/windowBlur';

const FilterPillWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1px;
  height: 30px;

  font-size: 13px;
  font-weight: 500;
  line-height: 21px;
  color: ${cssVar['color-text-primary-reverse']};

  /* --filter-pill-background: rgba(128, 0, 255, 1);
  --filter-pill-border: rgba(128, 0, 255, 1);
  --filter-pill-background-hover: rgba(88, 0, 175, 1);
  --filter-pill-border-hover: rgba(171, 127, 215, 1); */

  --filter-pill-background: ${cssVar['color-bg-primary-reverse']};
  --filter-pill-border: ${cssVar['color-bg-primary-reverse']};
  --filter-pill-background-hover: rgba(161, 161, 161, 1);
  --filter-pill-border-hover: rgba(255, 255, 255, 0.5);

  &:focus {
    outline: none;
  }
`;

const FilterPillName = styled.span`
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0px 6px 0px 8px;
  gap: 10px;
  border-radius: 8px 0px 0px 8px;
  background-color: var(--filter-pill-background);
  border: 2px solid var(--filter-pill-border);

  white-space: nowrap;

  ${mediaHover} {
    ${FilterPillWrapper}:focus & {
      border-color: var(--filter-pill-border-hover);
      background: var(--filter-pill-background-hover);
    }
  }
`;

const FilterPillContent = styled.div`
  display: flex;
  height: 30px;
  justify-content: center;
  align-items: center;
  border-radius: 0px 8px 8px 0px;
  flex-grow: 1;
  flex-shrink: 1;
  background-color: var(--filter-pill-background);
  width: fit-content;

  overflow: clip;

  ${mediaHover} {
    ${FilterPillWrapper}:focus & {
      border-color: var(--filter-pill-border-hover);
      background: var(--filter-pill-background-hover);
    }
  }
`;

const FilterPillValue = styled.span`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: text;
  padding: 0 6px 0 10px;

  border: 2px solid var(--filter-pill-border);
  border-right: none;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${mediaHover} {
    ${FilterPillWrapper}:focus & {
      border-color: var(--filter-pill-border-hover);
    }
  }
`;

const FilterPillCloseButton = styled.button`
  padding-right: 4px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  border: 2px solid var(--filter-pill-border);
  border-left: none;

  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;

  ${mediaHover} {
    ${FilterPillWrapper}:focus & {
      border-color: var(--filter-pill-border-hover);
    }
  }

  &:focus {
    outline: none;
  }
`;

const FilterPillCloseSpan = styled.span`
  border-radius: 4px;
  width: 17px;
  height: 17px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const FilterCloseIcon = styled(CloseIcon)`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  opacity: 0.5;
  transition: opacity 0.1s ease-in-out;

  ${FilterPillCloseButton}:focus &, ${FilterPillCloseButton}:hover &, ${FilterPillCloseButton}:focus-within &, &:hover {
    opacity: 1;
  }
`;

type FilterPillProps = {
  filter: SearchBarFilter;
  setFilter: (filter: SearchBarFilter) => void;
  deleteFilter: () => void;

  autoFocus?: boolean;
};

const FilterPill: React.FC<FilterPillProps> = ({ filter, autoFocus, setFilter, deleteFilter }) => {
  const [editing, setEditing] = useState(autoFocus);
  const ref = useRef<HTMLDivElement>(null);
  const { focusNextOption, focusPreviousOption, focusSearchInput } = useSearchBarActions();
  const { isMobileView } = useResponsive();

  const onStopEditing = () => {
    if (!editing) return;

    setEditing(false);
    if (filter.value === null) {
      if (document.activeElement === ref.current) {
        // This is in case the user backspaces an empty filter,
        // this would cause it to focus the pill (out of the combobox)
        // and delete itself, so we also focus the previous pill/input.
        focusPreviousOption();
      }
      deleteFilter();
    }
  };

  const onClickValue = () => {
    setEditing(true);
  };

  const onComboBoxUnfocus = () => {
    onStopEditing();
  };

  const onComboBoxEscape = () => {
    if (ref.current) {
      ref.current.focus();
    }
    onStopEditing();
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const element = e.currentTarget;

    chainShortcutHandlers(
      e,
      [
        /**
         * Handle enter or space keydown events and trigger the combobox (if not already open)
         */
        (e) => {
          if ((!isHotkey('enter', e) && !isHotkey('space', e) && !isHotkey('down', e)) || editing)
            return false;
          setEditing(true);

          return true;
        },
        /**
         * Handle the escape keydown event and close the combobox
         */
        (e) => {
          if (!isHotkey('escape', e)) return false;

          ref.current?.blur();

          return true;
        },
        /**
         * Tab keydown event replication but with smooth scrolling
         */
        (e) => {
          if (!isHotkey('tab', e) && !isHotkey('shift+tab', e)) return false;

          return Boolean(e.shiftKey ? focusPreviousOption() : focusNextOption());
        },
        /**
         * Handle the left arrow keydown event and focus the previous filter pill (role="option") or input (role="input")
         */
        (e) => {
          if (!isHotkey('left', e)) return false;

          // if focused on an input and not at the beginning of the input, ignore the left arrow key
          const comboboxInput = element.querySelector<HTMLInputElement>('input[role="combobox"]');
          if (comboboxInput?.matches(':focus') && comboboxInput.selectionStart !== 0) {
            return false;
          }

          return Boolean(focusPreviousOption());
        },
        /**
         * Handle the right arrow keydown event and focus the next filter pill (role="option") or input (role="input")
         */
        (e) => {
          if (!isHotkey('right', e)) return false;

          // if focused on an input and not at the end of the input, ignore the right arrow key
          const comboboxInput = element.querySelector<HTMLInputElement>('input[role="combobox"]');
          if (
            comboboxInput?.matches(':focus') &&
            comboboxInput.selectionStart !== comboboxInput.value.length
          ) {
            return false;
          }

          return Boolean(focusNextOption());
        },
        /**
         * Handle the backspace keydown event, if the combobox is closed we delete the filter,
         * if the combobox is open and there is no text in the input, we also delete the filter.
         * but if there is text in the input and we are on the left-most edge (and not selecting text),
         * we unfocus the input.
         */
        (e) => {
          if (!isHotkey('backspace', e)) return false;

          if (editing) {
            const comboboxInput = element.querySelector<HTMLInputElement>('input[role="combobox"]');
            if (
              comboboxInput?.matches(':focus') &&
              comboboxInput.selectionStart === 0 &&
              comboboxInput.selectionEnd === 0
            ) {
              comboboxInput.blur();
              ref.current?.focus();
              return true;
            }
          } else {
            focusPreviousOption();
            deleteFilter();
            return true;
          }

          return false;
        },
        /**
         * Handle the delete keydown event, same as the backspace keydown event but for the delete key.
         * Instead of the left-most edge, we check the right-most edge.
         */
        (e) => {
          if (!isHotkey('delete', e)) return false;

          if (editing) {
            const comboboxInput = element.querySelector<HTMLInputElement>('input[role="combobox"]');
            if (
              comboboxInput?.matches(':focus') &&
              comboboxInput.selectionStart === comboboxInput.value.length
            ) {
              comboboxInput.blur();
              ref.current?.focus();
              return true;
            }
          } else {
            deleteFilter();
            return true;
          }

          return false;
        },
      ],
      {
        preventDefaultOnHandle: true,
        stopPropagationOnHandle: true,
      },
    );
  };

  const onClickWrapper = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isInteractiveElementOrHasInteractiveParent(e.target as HTMLElement, true) && !editing)
      return;
    e.preventDefault();
    e.currentTarget.focus();
    e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  const onComboboxChange = (option: SearchBarFilterOption) => {
    setFilter({ ...filter, value: option });

    // Not using the onStopEditing because the filter value would still be null during this call.
    setEditing(false);

    if (!isMobileView) {
      // Focus the pill wrapper
      setTimeout(() => {
        // if was just created (value is null) we focus back to the search input
        if (filter.value === null) {
          focusSearchInput();
        } else {
          ref.current?.focus();
        }
      }, 0);
    } else {
      windowBlur();
    }
  };

  return (
    <FilterPillWrapper
      role="option"
      tabIndex={0}
      ref={ref}
      onKeyDown={onKeyDownHandler}
      onClick={onClickWrapper}
      onDoubleClick={onClickValue}
      data-control-id={`filter-combobox-${filter.$key}`}
    >
      <FilterPillName>{searchBarFilterConfigs[filter.type].name}</FilterPillName>
      {/* always render for animations to work*/}
      {isMobileView && (
        <FilterDropdownMobile
          filter={filter}
          open={editing ?? false}
          onChange={onComboboxChange}
          onOpenChange={(value) => {
            if (value === false) {
              onStopEditing();
            }
          }}
        />
      )}
      {editing ? (
        isMobileView ? null : (
          <FilterCombobox
            onBlur={onComboBoxUnfocus}
            onEscape={onComboBoxEscape}
            filter={filter}
            onChange={onComboboxChange}
            autoFocus
          />
        )
      ) : (
        <FilterPillContent>
          <FilterPillValue onClick={onClickValue} role="button" tabIndex={-1}>
            {filter.value?.label}
          </FilterPillValue>
          <FilterPillCloseButton tabIndex={-1} onClick={deleteFilter}>
            <FilterPillCloseSpan>
              <FilterCloseIcon />
            </FilterPillCloseSpan>
          </FilterPillCloseButton>
        </FilterPillContent>
      )}
    </FilterPillWrapper>
  );
};

export default FilterPill;
